import { isEqual } from 'lodash';
import React from 'react';

import { SummaryChangedEventData, SummaryChangedEventMeta } from '~src/bootstrap/types';
import { useGlobalStateQuery } from '~src/global-state';

import { useIpc } from '../ipc-utils';

export function useSummaryChangedEventEmitter() {
  const prevPayloadRef = React.useRef<SummaryChangedEventData | undefined>(undefined);
  const stateQuery = useGlobalStateQuery();
  const stateData = stateQuery.data;

  const ipc = useIpc();

  React.useEffect(() => {
    const sessions = stateData?.sessions;
    const prevPayload = prevPayloadRef.current;

    if (!sessions) return;

    const prices = sessions
      .map(session => {
        const choice = session.user_choice ?? session.preselected_choice;
        const selectedCategory = session.categories.find(
          category => category.id === choice.shipping_category
        );

        /*
         * Note: it is possible that preselected category does not exist.
         * If you want to prevent it set this flag in COS:
         *
         * "delete_pickup_options_without_location": true
         */
        if (!selectedCategory) {
          return;
        }

        const selectedOption = selectedCategory.options.find(
          option =>
            option.shipping_method === choice.shipping_method &&
            option.location?.id === choice.location_ref
        )!;

        return selectedOption.price.amount;
      })
      .filter(Boolean) as number[];

    const data: SummaryChangedEventData = {
      total_value: prices.reduce((total, value) => total + value, 0),
      delivery_address: stateData.recipient,
      billing_address: stateData.billing_address,
    };

    const meta: SummaryChangedEventMeta = {
      total_value_changed: !isEqual(data.total_value, prevPayload?.total_value),
      delivery_address_changed: !isEqual(data.delivery_address, prevPayload?.delivery_address),
      billing_address_changed: !isEqual(data.billing_address, prevPayload?.billing_address),
    };

    if (meta.total_value_changed || meta.delivery_address_changed || meta.billing_address_changed) {
      ipc.send({
        name: 'summary_changed',
        data: [data, meta],
      });
    }

    prevPayloadRef.current = data;
  }, [stateQuery.dataUpdatedAt]);
}
